// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Plugins
import {
  convertTemperature,
  formatValueWithUnit,
} from '../../../../../../../../plugins/general';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../../../store/measurement.store';

// Types
import {
  MeasurementType,
  TemperatureType,
} from '../../../../../../../../types/global';

// Components
import { EquipmentSensorListItem } from '../../../../../../../../components/EquipmentSensorList';
import { Row } from '../../../../../../../../layout/elements/Row';

// Modules
import { ListModule } from '../../../ListModule';

// View
import { CrusherProps } from './Crusher.types';
import { Container, ListCol, ListContainer } from './Crusher.style';

function Crusher({
  className,
  unitData,
  testId,
}: CrusherProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  const temperatureUnit: TemperatureType = getMeasurementUnit(MeasurementType.temperature);

  const crusherMotorItems: EquipmentSensorListItem[] = [
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.fullLoadDescription')}`,
      setpoint: formatValueWithUnit(unitData?.spCrusherMotorFullLoadAmps, 'A'),
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.serviceFactorDescription')}`,
      setpoint: formatValueWithUnit(unitData?.spCrusherMotorServiceFactor, 'SF', 2),
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.runningAmpsDescription')}`,
      setpoint: formatValueWithUnit(unitData?.spCrusherMotorRunningAmps, 'A'),
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.crushingAmpsDescription')}`,
      setpoint: formatValueWithUnit(unitData?.spCrusherMotorCrushingAmps, 'A'),
    },
  ];

  const warningHornItems: EquipmentSensorListItem[] = [
    ...(unitData?.eccentricBushingTempEnable ? [{
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.dischargeStartDescription')}`,
      setpoint: formatValueWithUnit(unitData?.spDischargeWarningHornTime, 's'),
    }] : []),

    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.crusherMotorStopDescription')}`,
      setpoint: formatValueWithUnit(unitData?.spCrusherMotorWarningHornTime, 's'),
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.feederStartDescription')}`,
      setpoint: formatValueWithUnit(unitData?.spFeederWarningHornTime, 's'),
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.crusherAdjustmentDescription')}`,
      setpoint: formatValueWithUnit(unitData?.spCrusherAdjustmentWarningHornTime, 's'),
    },
  ];

  const overItems: EquipmentSensorListItem[] = [
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.timeAboveFlaSF1.15Description')}`,
      setpoint: formatValueWithUnit(unitData?.spTimeAboveFlaSf115FeedStop, 's'),
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.timeAboveFlaDescription')}`,
      setpoint: formatValueWithUnit(unitData?.spTimeAboveFlaFeedStop, 's'),
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.timeAboveFlaSFDescription')}`,
      setpoint: formatValueWithUnit(unitData?.spTimeAboveFlaSfFeedStop, 's'),
    },
  ];

  const cavityItems: EquipmentSensorListItem[] = [
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.maxLevelDescription')}`,
      setpoint: formatValueWithUnit(unitData?.spCavityLevelMaxLevel, '%'),
    },
  ];

  const beltItems: EquipmentSensorListItem[] = [
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.beltSlipDescription')}`,
      setpoint: formatValueWithUnit(unitData?.spBeltSlipAlarmRpm, 'RPM'),
    },
  ];

  const counterItems: EquipmentSensorListItem[] = [
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.overTempWarningDescription')}`,
      setpoint: convertTemperature(
        unitData?.spCounterShaftTempWarning,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
  ];

  const eccentricItems: EquipmentSensorListItem[] = [
    ...(unitData?.eccentricBushingTempEnable ? [{
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.tempHighDescription')}`,
      setpoint: convertTemperature(
        unitData?.spEccentricBushingTempHigh,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    }] : []),
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.tempHighFeedDescription')}`,
      setpoint: convertTemperature(
        unitData?.spEccentricBushingTempHighStopFeed,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t('view.crusher.maintenance.setPoint.crusher.temperatureHighCrusherDescription')}`,
      setpoint: convertTemperature(
        unitData?.spEccentricBushingTempHighStopCrusher,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
  ];

  return (
    <Container className={className} data-testid={testId}>
      <Row>
        <ListContainer>
          <ListCol cols={24} lg={12}>
            <ListModule
              title={`${t('view.crusher.maintenance.setPoint.crusher.crusherMotorSpecsTitle')}`}
              items={crusherMotorItems}
            />
          </ListCol>
          <ListCol cols={24} lg={12}>
            <ListModule
              title={`${t('view.crusher.maintenance.setPoint.crusher.warningHornSecondsTitle')}`}
              items={warningHornItems}
            />
          </ListCol>
          <ListCol cols={24} lg={12}>
            <ListModule
              title={`${t('view.crusher.maintenance.setPoint.crusher.overCurrentProtectionTitle')}`}
              items={overItems}
            />
          </ListCol>
          {unitData?.cavityLevelSensorEnable && (
            <ListCol cols={24} lg={12}>
              <ListModule
                title={`${t('view.crusher.maintenance.setPoint.crusher.cavityLevelTitle')}`}
                items={cavityItems}
              />
            </ListCol>
          )}
          {unitData?.beltSlipMonitorEnable && (
            <ListCol cols={24} lg={12}>
              <ListModule
                title={`${t('view.crusher.maintenance.setPoint.crusher.beltSlipTitle')}`}
                items={beltItems}
              />
            </ListCol>
          )}
          {unitData?.counterShaftBushingTempEnable && (
            <ListCol cols={24} lg={12}>
              <ListModule
                title={`${t('view.crusher.maintenance.setPoint.crusher.counterShaftTempTitle')}`}
                items={counterItems}
              />
            </ListCol>
          )}
          {unitData?.eccentricBushingTempEnable && (
            <ListCol cols={24} lg={12}>
              <ListModule
                title={`${t('view.crusher.maintenance.setPoint.crusher.eccentricBushingTempTitle')}`}
                items={eccentricItems}
              />
            </ListCol>
          )}
        </ListContainer>
      </Row>
    </Container>
  );
}

export { Crusher };
